/**
 * Streaming response bubble. Renders a loading spinner while waiting
 * for the first text chunk, then fills in with the incoming stream.
 * Docked next to the bird at 60 fps. Mirrors the native
 * `CompanionResponseOverlay.swift` behavior — spring-bounce in, text
 * fades in, bubble auto-hides a few seconds after the final chunk.
 */

const BUBBLE_OFFSET_X_FROM_BIRD_CENTER = 26 + 12;
const POSITION_TICK_MS = 1000 / 60;
const AUTO_HIDE_DELAY_MS = 6000;

export interface ResponseBubbleOptions {
  getBirdCenter: () => { x: number; y: number };
}

export class ResponseBubble {
  private readonly bubbleElement: HTMLDivElement;
  private readonly contentElement: HTMLSpanElement;
  private readonly spinnerElement: HTMLSpanElement;
  private readonly options: ResponseBubbleOptions;
  private positionTimerId: number | null = null;
  private autoHideTimerId: number | null = null;
  private currentText = "";

  constructor(options: ResponseBubbleOptions) {
    this.options = options;
    this.bubbleElement = document.createElement("div");
    this.bubbleElement.className = "koko-pixel-box koko-response-bubble";

    const fillLayer = document.createElement("div");
    fillLayer.className = "koko-pixel-box-fill";

    const header = document.createElement("div");
    header.className = "koko-response-header";
    const label = document.createElement("span");
    label.className = "koko-label";
    label.textContent = "KOKO";
    const escHint = document.createElement("span");
    escHint.className = "koko-esc-hint";
    escHint.textContent = "esc";
    header.appendChild(label);
    header.appendChild(escHint);

    this.spinnerElement = document.createElement("span");
    this.spinnerElement.className = "koko-spinner";
    this.spinnerElement.textContent = ".  ";

    this.contentElement = document.createElement("span");
    this.contentElement.className = "koko-content";

    fillLayer.appendChild(header);
    fillLayer.appendChild(this.spinnerElement);
    fillLayer.appendChild(this.contentElement);
    this.bubbleElement.appendChild(fillLayer);

    this.startSpinnerDotAnimation();
  }

  private spinnerDotCount = 1;
  private spinnerIntervalId: number | null = null;

  private startSpinnerDotAnimation(): void {
    if (this.spinnerIntervalId !== null) return;
    this.spinnerIntervalId = window.setInterval(() => {
      this.spinnerDotCount = (this.spinnerDotCount % 3) + 1;
      if (this.spinnerElement.style.display !== "none") {
        this.spinnerElement.textContent = ".".repeat(this.spinnerDotCount);
      }
    }, 400);
  }

  mount(container: HTMLElement): void {
    container.appendChild(this.bubbleElement);
  }

  /** Begin a new response cycle. Shows the spinner immediately. */
  beginStreaming(): void {
    this.cancelAutoHide();
    this.currentText = "";
    this.contentElement.textContent = "";
    this.spinnerElement.style.display = "inline-flex";
    this.positionToBird();
    this.bubbleElement.classList.add("visible");
    this.startPositionTimer();
  }

  /**
   * Replace the bubble's current text with the provided string. The
   * caller is expected to have already stripped any [POINT:...] tag.
   */
  updateStreaming(text: string): void {
    if (text.length > 0 && this.spinnerElement.style.display !== "none") {
      this.spinnerElement.style.display = "none";
    }
    this.currentText = text;
    this.contentElement.textContent = text;
  }

  /**
   * Append a spoken segment's text to the bubble. Used by the chunked
   * TTS pipeline to reveal each sentence in lockstep with its audio,
   * so the bubble doesn't race ahead of the spoken word. Segments
   * already include leading whitespace from the original stream
   * tokenization, so we don't add our own separator.
   */
  revealSpokenSegment(segmentText: string): void {
    if (this.spinnerElement.style.display !== "none") {
      this.spinnerElement.style.display = "none";
    }
    const next = this.currentText.length === 0 ? segmentText : this.currentText + segmentText;
    this.currentText = next;
    this.contentElement.textContent = next;
  }

  /** Mark streaming complete and schedule the bubble to auto-hide. */
  finishStreaming(): void {
    if (this.currentText.length === 0) {
      this.hide();
      return;
    }
    this.cancelAutoHide();
    this.autoHideTimerId = window.setTimeout(() => this.hide(), AUTO_HIDE_DELAY_MS);
  }

  hide(): void {
    this.bubbleElement.classList.remove("visible");
    this.stopPositionTimer();
    this.cancelAutoHide();
  }

  destroy(): void {
    this.hide();
    if (this.spinnerIntervalId !== null) {
      window.clearInterval(this.spinnerIntervalId);
      this.spinnerIntervalId = null;
    }
    this.bubbleElement.remove();
  }

  private startPositionTimer(): void {
    if (this.positionTimerId !== null) return;
    this.positionTimerId = window.setInterval(
      () => this.positionToBird(),
      POSITION_TICK_MS,
    );
  }

  private stopPositionTimer(): void {
    if (this.positionTimerId !== null) {
      window.clearInterval(this.positionTimerId);
      this.positionTimerId = null;
    }
  }

  private cancelAutoHide(): void {
    if (this.autoHideTimerId !== null) {
      window.clearTimeout(this.autoHideTimerId);
      this.autoHideTimerId = null;
    }
  }

  private positionToBird(): void {
    const birdCenter = this.options.getBirdCenter();
    const bubbleWidth = this.bubbleElement.offsetWidth || 280;
    const bubbleHeight = this.bubbleElement.offsetHeight || 48;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let leftEdge = birdCenter.x + BUBBLE_OFFSET_X_FROM_BIRD_CENTER;
    let transformOrigin = "left center";
    if (leftEdge + bubbleWidth > viewportWidth - 16) {
      leftEdge = birdCenter.x - BUBBLE_OFFSET_X_FROM_BIRD_CENTER - bubbleWidth;
      transformOrigin = "right center";
    }
    leftEdge = Math.max(8, leftEdge);

    let topEdge = birdCenter.y - bubbleHeight / 2;
    topEdge = Math.max(8, Math.min(viewportHeight - bubbleHeight - 8, topEdge));

    this.bubbleElement.style.transformOrigin = transformOrigin;
    // Position via left/top so CSS `transform: scale(...)` can own the
    // spring bounce animation without being overwritten every tick.
    this.bubbleElement.style.left = `${leftEdge}px`;
    this.bubbleElement.style.top = `${topEdge}px`;
  }
}
